// src/components/ProjectCallToAction.tsx
//
// Closing call to action.
//
// The component carries no page-specific assumptions: copy and destinations
// come from props, with a default set aimed at prospective clients. Drop
// `<ProjectCallToAction />` anywhere inside the router for the default, or
// override any part of the content to retarget it.
//
// Copy rules applied here:
//   - No performance, ranking or outcome claims. Nothing is described in
//     terms of what was achieved for a client, because no such record exists
//     to cite.
//   - Availability and process are not stated, because they are not known.
//   - The visitor is addressed as an engineer with a requirement, not as a
//     consumer being sold to.
import { useId } from 'react';
import { useReducedMotion } from 'framer-motion';

import { STAGGER, motionSet } from './about/motion';
import {
  CtaRoot,
  CtaContainer,
  Panel,
  Eyebrow,
  Heading,
  Message,
  Actions,
  ActionLink,
  ActionAnchor,
} from './ProjectCallToAction.styles';

/** One navigable action. `to` is an internal route unless it is a full URL. */
export interface CtaActionConfig {
  label: string;
  to: string;
}

export interface ProjectCallToActionContent {
  eyebrow: string;
  heading: string;
  message: string;
  primaryAction: CtaActionConfig;
  secondaryAction: CtaActionConfig;
}

export interface ProjectCallToActionProps {
  /** Shallow-merged over the defaults, so a partial override is enough. */
  content?: Partial<ProjectCallToActionContent>;
  className?: string;
}

export const defaultCtaContent: ProjectCallToActionContent = {
  eyebrow: 'Enquiries',
  // Positions the visit as a system with a requirement attached, which is the
  // common case for the work: an existing application, or a backend to build.
  heading: 'Have a system to build or improve?',
  // Enumerates the requirement types this CTA is aimed at, plainly and without
  // promising results. "New build" and "existing application" are named
  // separately because the two arrive with very different starting points.
  message:
    'I work on concrete software requirements: backend systems, APIs, third-party integrations, database design and application workflows. That applies equally to a new build and to an existing application that needs extending, reworking or fixing.',
  // Existing routes: /get-in-touch is the enquiry form, /projects is the
  // canonical work index.
  primaryAction: { label: "Let's Discuss the Project", to: '/get-in-touch' },
  secondaryAction: { label: 'View My Work', to: '/projects' },
};

const isExternal = (to: string) => /^https?:\/\//i.test(to);

const Action = ({
  action,
  variant,
}: {
  action: CtaActionConfig;
  variant: 'primary' | 'secondary';
}) => {
  if (isExternal(action.to)) {
    return (
      <ActionAnchor
        $variant={variant}
        href={action.to}
        target="_blank"
        rel="noopener noreferrer"
      >
        {action.label}
      </ActionAnchor>
    );
  }

  return (
    <ActionLink $variant={variant} to={action.to}>
      {action.label}
    </ActionLink>
  );
};

const ProjectCallToAction = ({ content, className }: ProjectCallToActionProps) => {
  const reduceMotion = useReducedMotion() ?? false;
  const anim = motionSet(reduceMotion);
  const headingId = useId();
  const viewport = { once: true, amount: 0.3 } as const;

  const { eyebrow, heading, message, primaryAction, secondaryAction } = {
    ...defaultCtaContent,
    ...content,
  };

  return (
    <CtaRoot className={className} aria-labelledby={headingId}>
      <CtaContainer>
        <Panel
          variants={anim.group(STAGGER.group)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          <Eyebrow variants={anim.reveal}>{eyebrow}</Eyebrow>
          <Heading id={headingId} variants={anim.reveal}>
            {heading}
          </Heading>
          <Message variants={anim.reveal}>{message}</Message>

          <Actions variants={anim.reveal}>
            <Action action={primaryAction} variant="primary" />
            <Action action={secondaryAction} variant="secondary" />
          </Actions>
        </Panel>
      </CtaContainer>
    </CtaRoot>
  );
};

export default ProjectCallToAction;
